import { Alert } from '@designsforhealth/dfh-react-components'
import { CircularProgress, Grid } from '@material-ui/core'
import React, { useMemo, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

import { PostCustomerDispensaryPromoDocument } from '../../../../../graphql/camel'
import useCamelMutation from '../../../../../hooks/useCamelMutation'
import useLogAndCaptureError from '../../../../../hooks/useLogAndCaptureError'
import { RequiredSelectField, RequiredTextField } from '../../../../form/RequiredFields'
import TextField from '../../../../form/TextField'
import { StyledAddPromoHeader, StyledFieldContainer, StyledPrimaryButton } from './styled'
import { MutatePromoProps } from './types'

export type DispensaryAddNewPromoProps = MutatePromoProps

interface AddPromoFormValues {
  dispensaryCode: string
  rootPromoId: string
  expirationDate: string
}

const DispensaryAddNewPromo: React.FC<DispensaryAddNewPromoProps> = ({
  dispensarySlug,
  existingCodes,
  existingOneTimeOnlyIds,
  rootPromos,
  refetchQueries,
}) => {
  const methods = useForm<AddPromoFormValues>()
  const { formState, handleSubmit, setError, errors, reset } = methods
  const [errorHelper, setErrorHelper] = useState('')
  const [postDispensaryPromo, { loading, error }] = useCamelMutation(
    PostCustomerDispensaryPromoDocument,
    {
      refetchQueries,
    },
  )

  // TODO: filter out bad request (e.g. conflict) errors
  useLogAndCaptureError(error)

  const rootPromoOptions = useMemo(
    () =>
      rootPromos.reduce((acc: { label: string; value: string }[], rootPromo) => {
        if (!rootPromo) return acc
        if (existingOneTimeOnlyIds.includes(rootPromo.id)) return acc
        acc.push({
          label: rootPromo.name,
          value: rootPromo.id,
        })
        return acc
      }, []),
    [existingOneTimeOnlyIds, rootPromos],
  )

  const onSubmit: SubmitHandler<AddPromoFormValues> = async (data) => {
    if (errorHelper) {
      // Reset error helper if it was set from a previous attempt.
      setErrorHelper('')
    }
    if (/[^a-zA-Z0-9#!_\-%&*$/\\]/.test(data.dispensaryCode)) {
      setError('dispensaryCode', {
        message: 'Invalid characters.',
        type: 'validate',
      })
      setErrorHelper(
        'Only alphanumeric characters and the following special characters are allowed: # ! _ - % & * $ / \\',
      )
      return
    }
    if (existingCodes.includes(data.dispensaryCode)) {
      setError('dispensaryCode', { message: 'Code already exists', type: 'validate' })
      return
    }
    const { errors: mutationErrors } = await postDispensaryPromo({
      variables: {
        dispensarySlug,
        customerDispensaryPromoNewInput: {
          dispensaryCode: data.dispensaryCode,
          rootPromoId: data.rootPromoId,
          expirationDate: data.expirationDate,
        },
      },
    })
    if (mutationErrors && mutationErrors.length) {
      return
    }

    reset()
  }

  return (
    <>
      <StyledAddPromoHeader>Add New Promotion:</StyledAddPromoHeader>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3} justify="space-between" alignItems="center">
            <Grid item xs={12} sm={3}>
              <StyledFieldContainer>
                <RequiredTextField name="dispensaryCode" label="Code" />
              </StyledFieldContainer>
            </Grid>
            <Grid item xs={12} sm={3}>
              <StyledFieldContainer>
                <RequiredSelectField
                  name="rootPromoId"
                  label="Promotion"
                  options={rootPromoOptions}
                />
              </StyledFieldContainer>
            </Grid>
            <Grid item xs={12} sm={4}>
              <StyledFieldContainer>
                <TextField
                  name="expirationDate"
                  label="Expiration Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ inputProps: { min: new Date().toISOString().substring(0, 10) } }}
                />
              </StyledFieldContainer>
            </Grid>
            <Grid item xs={12} sm={2}>
              <StyledPrimaryButton type="submit" disabled={formState.isSubmitting || loading}>
                {formState.isSubmitting || loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Add'
                )}
              </StyledPrimaryButton>
            </Grid>
            {/* TODO: handle conflict error */}
            {!!error && (
              <Grid item xs={12}>
                <Alert type="error">
                  An error occurred creating promotion. Please try again soon.
                </Alert>
              </Grid>
            )}
            {!!errors.dispensaryCode?.message && (
              <Grid item xs={12}>
                <Alert type="error">
                  {errors.dispensaryCode.message + `${errorHelper ? ' ' + errorHelper : ''}`}
                </Alert>
              </Grid>
            )}
          </Grid>
        </form>
      </FormProvider>
    </>
  )
}

export default DispensaryAddNewPromo
